import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFetcher } from '@remix-run/react';
import type { AddCollaboratorsMutation } from 'generated/graphql';
import { ADD_BUILD_HUB_COLLABORATORS_API_ROUTE } from '~/routes/remix-api.app.build-hub-collaborator-add';

interface UseBuildHubCollaboratorsAddOptions {
    onSuccess?: () => void;
}

interface UseBuildHubCollaboratorsAddReturn {
    inviteCollaborators: (formData: FormData) => void;
    isUpdating: boolean;
    error: string | null;
    isSuccess: boolean;
    isIdle: boolean;
}

/**
 * Hook to handle collaborator invite submissions using Remix fetcher
 */
export function useBuildHubCollaboratorAdd(
    options: UseBuildHubCollaboratorsAddOptions = {}
): UseBuildHubCollaboratorsAddReturn {
    const { onSuccess } = options;
    const inviteFetcher = useFetcher<AddCollaboratorsMutation>();
    const data = inviteFetcher.data?.buildHubCollaboratorAdd;
    const isUpdating = inviteFetcher.state !== 'idle';
    const isIdle = !isUpdating;
    const isSuccess = Boolean(isIdle && data && data.__typename === 'BuildHub');

    // Track the last successful submission to prevent duplicate onSuccess calls
    const lastSuccessDataRef = useRef<typeof data | null>(null);

    const inviteCollaborators = useCallback(
        (formData: FormData) => {
            inviteFetcher.submit(formData, {
                method: 'post',
                action: ADD_BUILD_HUB_COLLABORATORS_API_ROUTE,
            });
        },
        [inviteFetcher]
    );

    const error = useMemo(() => {
        if (isIdle && data && data.__typename !== 'BuildHub') {
            return 'Message' in data
                ? data.Message
                : 'Failed to invite collaborators';
        }
        return null;
    }, [isIdle, data]);

    // Success handler - only fire once per successful submission
    useEffect(() => {
        if (isSuccess && data !== lastSuccessDataRef.current) {
            lastSuccessDataRef.current = data;
            onSuccess?.();
        }
    }, [isSuccess, data, onSuccess]);

    return {
        inviteCollaborators,
        isUpdating,
        error,
        isSuccess,
        isIdle,
    };
}
